import { LoadingButton } from "@mui/lab";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import { IconHelp, IconSend, IconX } from "@tabler/icons-react";
import { useState } from "react";
import Functions from "../../../../callable/functions";
import felixAnalytics from "../../../../felixAnalytics";
import Usuario from "../../../../models/usuario";
import { useSnackBar } from "../../../../stores/snackBarStores";

type Props = {
  onClose: (success: boolean) => void;
  open: boolean;
  cliente: Usuario;
};

export default function ConvidarClienteDialog({
  onClose,
  open,
  cliente,
}: Props) {
  const [email, setEmail] = useState("");
  const [openFaq, setOpenFaq] = useState(false);
  const [enviandoConvite, setEnviandoConvite] = useState(false);
  const snackBar = useSnackBar();

  return (
    <>
      <Dialog onClose={onClose} open={open} maxWidth="xs" fullWidth>
        <DialogTitle color="text.primary">
          <Box
            display="flex"
            flexDirection="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <Box display="flex" flexDirection="row" alignItems="center">
              <Box mr={1}>
                <IconSend size={16} />
              </Box>
              <Typography variant="h4">
                {`Enviar convite para ${cliente.nome}`}
              </Typography>
            </Box>
            <IconButton
              edge="end"
              aria-label="close"
              size="small"
              onClick={() => {
                felixAnalytics.track("convite-faq");
                setOpenFaq(true);
              }}
            >
              <IconHelp />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ py: 1 }}>
          <TextField
            sx={{ my: 1 }}
            size="small"
            id="email"
            label="Email"
            name="email"
            autoComplete="email"
            value={cliente?.email ?? email}
            disabled={!!cliente?.email}
            onChange={(e) => setEmail(e.target.value)}
            fullWidth
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => onClose(false)} color="inherit">
            Cancelar
          </Button>
          <LoadingButton
            size="small"
            color="primary"
            variant="contained"
            loading={enviandoConvite}
            onClick={async () => {
              if (!!cliente) {
                setEnviandoConvite(true);
                felixAnalytics.track("convite-cliente-enviar");
                await new Functions()
                  .enviarConviteCliente({
                    id: cliente.id,
                    nome: cliente.nome,
                    email,
                  })
                  .then(() => {
                    snackBar({
                      type: "ALERT",
                      payload: {
                        mensagem: `O cliente ${cliente.nome} foi editado.`,
                        severidade: "success",
                      },
                    });
                  })
                  .catch((error) => {
                    snackBar({
                      type: "ALERT",
                      payload: {
                        mensagem:
                          error.details.code === "auth/email-already-exists"
                            ? `Esse e-mail já está cadastrado na Felix.`
                            : `Houve um erro ao enviar o convite para o cliente. Por favor, tente novamente ou entre em contato com nosso suporte.`,
                        severidade: "error",
                      },
                    });
                    console.error(error);
                  })
                  .finally(() => setEnviandoConvite(false))
                  .finally(() => onClose(true));
              }
            }}
          >
            Convidar
          </LoadingButton>
        </DialogActions>
      </Dialog>

      <Dialog maxWidth="md" open={openFaq} onClose={() => setOpenFaq(false)}>
        <DialogTitle color="text.primary">
          <Box
            display="flex"
            flexDirection="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <Box display="flex" flexDirection="row" alignItems="center">
              <Box mr={1}>
                <IconHelp size={16} />
              </Box>
              <Typography variant="h4">
                FAQ
              </Typography>
            </Box>
            <IconButton
              edge="end"
              aria-label="close"
              size="small"
              onClick={() => setOpenFaq(false)}
            >
              <IconX />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="h4" color="text.primary" gutterBottom>
            Quem devo convidar?
          </Typography>
          <Typography variant="body1" color="text.secondary" pb={2}>
            Contadores e clientes podem compartilhar informações sobre os
            investimentos.
          </Typography>
          <Typography variant="h4" color="text.primary" gutterBottom>
            O que será compartilhado?
          </Typography>
          <Typography variant="body1" color="text.secondary" pb={2}>
            Notas de corretagem, evolução do patrimônio e análises de desempenho
            dos investimentos. As informações contábeis não são compartilhadas.
          </Typography>
          <Typography variant="h4" color="text.primary" gutterBottom>
            Como isso me ajuda?
          </Typography>
          <Typography variant="body1" color="text.secondary" pb={2}>
            Você poderá pedir para que seu cliente mantenha as notas de
            corretagem sempre atualizadas na plataforma, podendo até mesmo
            conectar diretamente com sua corretora de forma totalmente segura.
          </Typography>
          <Typography variant="h4" color="text.primary" gutterBottom>
            Como isso ajuda meu cliente?
          </Typography>
          <Typography variant="body1" color="text.secondary" pb={2}>
            Seu cliente terá muito mais informações sobre seus investimentos.
            Poderá acompanhar seu patrimônio em tempo real aumentando ainda mais
            a confiança que já tem com você, contador.
          </Typography>
          <Typography variant="h4" color="text.primary" gutterBottom>
            E quanto custa?
          </Typography>
          <Typography variant="body1" color="text.secondary" pb={2}>
            Tudo isso sem nenhum custo adicional. Você poderá oferecer um
            serviço muito mais completo e seguro para seu cliente com a
            assinatura que já possui atualmente.
          </Typography>
        </DialogContent>
      </Dialog>
    </>
  );
}
